import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import type { Decimal } from '@insio/decimal';
import type { IDocumentExchangeRate } from '@insio/shared-types';
import type { VatRateOption } from '../components/inputs/VatRateSelector';
import type { RecomputeInvoiceLineFn } from '../modules/forms/invoice-lines/hooks/use-line-amounts-recompute';
import type { RecomputeVatSummaryFn } from '../modules/forms/vat-summary/hooks';
import type { InvoiceTotalsExpected } from '../modules/forms/invoice-totals/InvoiceTotalsForm';
import type { InvoiceLineExtraField } from '../modules/forms/invoice-lines/invoice-line-extra-field';

/** Host-supplied fetch of the suggested CNB/ECB rate for a live accounting-currency selection. */
export type FetchSuggestedExchangeRateFn = (
  accountingCurrencyCode: string
) => Promise<IDocumentExchangeRate | null>;

interface InvoiceContextValue {
  /**
   * Per-invoice (or per-document) VAT rate options the host app derives from
   * the invoice's supplier country and tax date. Empty array means the
   * country/date combination is unsupported — editors fall back to a freeform
   * `%` input.
   */
  vatRateOptions: readonly VatRateOption[];
  /** ISO 4217 currency of the invoice itself. */
  invoiceCurrencyCode: string | null;
  /**
   * ISO 4217 currency in which the invoice VAT summary amounts are expressed.
   * Differs from `invoiceCurrencyCode` on cross-border invoices where the VAT
   * breakdown is reported in the local accounting currency.
   */
  vatSummaryCurrencyCode: string | null;
  /**
   * Exchange rate stated on the document — units of `vatSummaryCurrencyCode`
   * per 1 unit of `invoiceCurrencyCode`.
   */
  vatSummaryExchangeRate: IDocumentExchangeRate | null;
  /**
   * ISO 4217 accounting currency the invoice converts into via
   * `accountingExchangeRate`. Resolved from the supplier (ISSUED) or customer
   * (RECEIVED) party — matches the backend's foreign-currency detection in
   * `resolveAccountingExchangeRate`. Drives whether the editor exposes the
   * exchange-rate field.
   */
  accountingCurrencyCode: string | null;
  /**
   * Whether line `unitPrice` values on the surrounding invoice are entered as
   * gross (VAT-inclusive) or net. Single convention per invoice — no per-line
   * override. Read by formatters and the line-items editor.
   */
  isUnitPriceInclVat: boolean;
  /**
   * Sum of `totalAmountInclVat` across the invoice's line items. The VAT
   * summary editor uses this to render a mismatch warning when the breakdown
   * gross total disagrees with the line items. Undefined disables the
   * cross-check.
   */
  lineItemsTotal?: Decimal;
  /**
   * Persisted invoice gross total. Lets the VAT summary editor cross-check
   * the saved breakdown against the actual invoice total rather than against
   * a value reconstructed from the rows. Null/undefined for invoices that
   * don't have totals yet — editor falls back to row-derived bootstrap.
   */
  invoiceTotalAmountInclVat?: Decimal | null;
  /** Persisted invoice net total. Same rationale as `invoiceTotalAmountInclVat`. */
  invoiceTotalAmountNoVat?: Decimal | null;
  /**
   * Host-supplied recompute callback for invoice line amounts. Wired into the
   * line-items editor via `useLineAmountsRecompute` (debouncing, dedup, and
   * stale-response handling live there). Undefined means the editor renders
   * without backend-derived warning overlays — safe default for hosts that
   * haven't opted in.
   */
  onRecomputeLine?: RecomputeInvoiceLineFn;
  /**
   * Host-supplied recompute callback for the VAT summary, backed by
   * `invoiceComputedVatSummary` (the line-derived breakdown). Wired into the
   * VAT summary editor to drive expected-value hints and the "fill from lines"
   * action. Undefined means the editor falls back to local net↔vat math.
   */
  onRecomputeVatSummary?: RecomputeVatSummaryFn;
  /**
   * Whether the invoice is a VAT document.
   */
  isVatDocument?: boolean | null;
  /** Totals summed from the VAT summary, in invoice currency. Drives the
   * invoice-totals editor's "fill from VAT summary" button. */
  expectedTotalsFromVatSummary?: InvoiceTotalsExpected | null;
  /** Totals summed from the line items, in invoice currency. Drives the
   * invoice-totals editor's "fill from line items" button. */
  expectedTotalsFromLines?: InvoiceTotalsExpected | null;
  /** Host-supplied fetch of the suggested exchange rate. Undefined disables the hint. */
  onFetchSuggestedExchangeRate?: FetchSuggestedExchangeRateFn;
  /** Host fields drawn on every line in the line-items editor. Undefined draws none. */
  lineFields?: readonly InvoiceLineExtraField[];
}

const InvoiceContext = createContext<InvoiceContextValue>({
  vatRateOptions: [],
  invoiceCurrencyCode: null,
  vatSummaryCurrencyCode: null,
  vatSummaryExchangeRate: null,
  accountingCurrencyCode: null,
  isUnitPriceInclVat: false,
  isVatDocument: null
});

export interface InvoiceProviderProps {
  children: ReactNode;
  vatRateOptions: readonly VatRateOption[];
  invoiceCurrencyCode?: string | null;
  vatSummaryCurrencyCode?: string | null;
  vatSummaryExchangeRate?: IDocumentExchangeRate | null;
  accountingCurrencyCode?: string | null;
  isUnitPriceInclVat?: boolean;
  lineItemsTotal?: Decimal;
  invoiceTotalAmountInclVat?: Decimal | null;
  invoiceTotalAmountNoVat?: Decimal | null;
  onRecomputeLine?: RecomputeInvoiceLineFn;
  onRecomputeVatSummary?: RecomputeVatSummaryFn;
  isVatDocument?: boolean | null;
  expectedTotalsFromVatSummary?: InvoiceTotalsExpected | null;
  expectedTotalsFromLines?: InvoiceTotalsExpected | null;
  onFetchSuggestedExchangeRate?: FetchSuggestedExchangeRateFn;
  lineFields?: readonly InvoiceLineExtraField[];
}

/**
 * Scoped provider for per-invoice editor context (VAT rate options + invoice
 * currency codes + invoice-wide `isUnitPriceInclVat` flag). Wrap an editable
 * invoice region (e.g. the invoice detail card) so editors opened from inside
 * that region — including modal-based ones reached via
 * `<FieldEditor fieldType="invoiceLines">` — can resolve the correct
 * country/date-specific rates, currency formatting, and pricing convention
 * without prop drilling through the generic FieldEditor API.
 *
 * Defaults outside any provider: empty rate options, null currency codes,
 * `isUnitPriceInclVat=false` — safe defaults for consumers.
 */
export const InvoiceProvider: React.FC<InvoiceProviderProps> = ({
  children,
  vatRateOptions,
  invoiceCurrencyCode = null,
  vatSummaryCurrencyCode = null,
  vatSummaryExchangeRate = null,
  accountingCurrencyCode = null,
  isUnitPriceInclVat = false,
  lineItemsTotal,
  invoiceTotalAmountInclVat,
  invoiceTotalAmountNoVat,
  onRecomputeLine,
  onRecomputeVatSummary,
  isVatDocument = null,
  expectedTotalsFromVatSummary = null,
  expectedTotalsFromLines = null,
  onFetchSuggestedExchangeRate,
  lineFields
}) => {
  const value = useMemo(
    () => ({
      vatRateOptions,
      invoiceCurrencyCode,
      vatSummaryCurrencyCode,
      vatSummaryExchangeRate,
      accountingCurrencyCode,
      isUnitPriceInclVat,
      lineItemsTotal,
      invoiceTotalAmountInclVat,
      invoiceTotalAmountNoVat,
      onRecomputeLine,
      onRecomputeVatSummary,
      isVatDocument,
      expectedTotalsFromVatSummary,
      expectedTotalsFromLines,
      onFetchSuggestedExchangeRate,
      lineFields
    }),
    [
      vatRateOptions,
      invoiceCurrencyCode,
      vatSummaryCurrencyCode,
      vatSummaryExchangeRate,
      accountingCurrencyCode,
      isUnitPriceInclVat,
      lineItemsTotal,
      invoiceTotalAmountInclVat,
      invoiceTotalAmountNoVat,
      onRecomputeLine,
      onRecomputeVatSummary,
      isVatDocument,
      expectedTotalsFromVatSummary,
      expectedTotalsFromLines,
      onFetchSuggestedExchangeRate,
      lineFields
    ]
  );
  return <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>;
};

export const useInvoiceContext = (): InvoiceContextValue => useContext(InvoiceContext);

export const useInvoiceVatRateOptions = (): readonly VatRateOption[] =>
  useContext(InvoiceContext).vatRateOptions;

export const useInvoiceCurrencyCode = (): string | null =>
  useContext(InvoiceContext).invoiceCurrencyCode;

export const useVatSummaryCurrencyCode = (): string | null =>
  useContext(InvoiceContext).vatSummaryCurrencyCode;

export const useVatSummaryExchangeRate = (): IDocumentExchangeRate | null =>
  useContext(InvoiceContext).vatSummaryExchangeRate;

export const useInvoiceAccountingCurrencyCode = (): string | null =>
  useContext(InvoiceContext).accountingCurrencyCode;

export const useInvoiceIsUnitPriceInclVat = (): boolean =>
  useContext(InvoiceContext).isUnitPriceInclVat;

export const useInvoiceLineItemsTotal = (): Decimal | undefined =>
  useContext(InvoiceContext).lineItemsTotal;

export const useInvoiceTotalAmountInclVat = (): Decimal | null | undefined =>
  useContext(InvoiceContext).invoiceTotalAmountInclVat;

export const useInvoiceTotalAmountNoVat = (): Decimal | null | undefined =>
  useContext(InvoiceContext).invoiceTotalAmountNoVat;

export const useInvoiceRecomputeVatSummary = (): RecomputeVatSummaryFn | undefined =>
  useContext(InvoiceContext).onRecomputeVatSummary;

export const useInvoiceRecomputeLine = (): RecomputeInvoiceLineFn | undefined =>
  useContext(InvoiceContext).onRecomputeLine;

export const useInvoiceIsVatDocument = (): boolean =>
  useContext(InvoiceContext).isVatDocument ?? true;

export const useInvoiceExpectedTotalsFromVatSummary = (): InvoiceTotalsExpected | null =>
  useContext(InvoiceContext).expectedTotalsFromVatSummary ?? null;

export const useInvoiceExpectedTotalsFromLines = (): InvoiceTotalsExpected | null =>
  useContext(InvoiceContext).expectedTotalsFromLines ?? null;

export const useInvoiceFetchSuggestedExchangeRate = (): FetchSuggestedExchangeRateFn | undefined =>
  useContext(InvoiceContext).onFetchSuggestedExchangeRate;

export const useInvoiceLineFields = (): readonly InvoiceLineExtraField[] | undefined =>
  useContext(InvoiceContext).lineFields;
